import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type ComplianceItemOutput,
  type NoteOutput,
  type UserOutput,
  type TimeEntryOutput,
  type UpdateComplianceItemInput,
  ITEM_STATUS_LABELS,
  DATA_STATUS_LABELS,
  STATUS_BADGE_CLASSES,
  DATA_STATUS_BADGE_CLASSES,
  JURISDICTION_LABELS,
} from '@1financial/shared';
import { useAuth, getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError, isHttpStatus } from '../lib/http-error.js';
import { ForbiddenPage } from '../components/ForbiddenPage.js';
import { EntityTags } from '../components/EntityTags.js';
import { ComplianceItemFormModal, ConfirmModal } from './CompliancePage.js';
import { fmtDate } from '../lib/date.js';
import { useCapabilities } from '../hooks/useCapabilities.js';

type Tab = 'overview' | 'notes' | 'time';

export function ComplianceItemDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { api, user: currentUser } = useAuth();
  const { canDo } = useCapabilities();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('overview');
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const update = useMutation({
    mutationFn: async (patch: UpdateComplianceItemInput) => {
      const res = await fetch(`/v1/compliance/items/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance-item', id] });
      queryClient.invalidateQueries({ queryKey: ['compliance-items'] });
      queryClient.invalidateQueries({ queryKey: ['my-compliance-items'] });
      setShowEdit(false);
    },
  });

  const remove = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/v1/compliance/items/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance-items'] });
      queryClient.invalidateQueries({ queryKey: ['my-compliance-items'] });
      navigate('/compliance');
    },
  });

  const item = useQuery({
    queryKey: ['compliance-item', id],
    enabled: !!id && api !== null,
    queryFn: async () => {
      const res = await fetch(`/v1/compliance/items/${id}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: ComplianceItemOutput };
      return body.data;
    },
  });

  const client = useQuery({
    queryKey: ['compliance-client', item.data?.clientId],
    enabled: !!item.data?.clientId,
    queryFn: async () => {
      const res = await fetch(`/v1/compliance/clients/${item.data!.clientId}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { id: string; name: string; company: string | null; engagementId: string | null } };
      return body.data;
    },
  });

  const users = useQuery({
    queryKey: ['users', { limit: 100 }],
    enabled: ['overview', 'notes', 'time'].includes(tab) && api !== null,
    queryFn: async () => {
      const res = await fetch('/v1/users?limit=100', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: UserOutput[] } };
      return body.data.items;
    },
  });

  const notes = useQuery({
    queryKey: ['notes', { scope: 'COMPLIANCE_ITEM', scopeId: id }],
    enabled: tab === 'notes' && !!id,
    queryFn: async () => {
      const res = await fetch(`/v1/notes?scope=COMPLIANCE_ITEM&scopeId=${id}&limit=100`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: NoteOutput[] } };
      return body.data.items;
    },
  });

  const timeEntries = useQuery({
    queryKey: ['time-entries', { complianceItemId: id }],
    enabled: tab === 'time' && !!id,
    queryFn: async () => {
      const res = await fetch(`/v1/time-entries?complianceItemId=${id}&limit=100`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: TimeEntryOutput[]; total: number } };
      return body.data;
    },
  });

  const action = useMutation({
    mutationFn: async (a: string) => {
      const res = await fetch(`/v1/compliance/items/${id}/action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify({ action: a }),
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance-item', id] });
      queryClient.invalidateQueries({ queryKey: ['compliance-items'] });
      queryClient.invalidateQueries({ queryKey: ['my-compliance-items'] });
    },
  });

  const createNote = useMutation({
    mutationFn: async (body: { title?: string; bodyMarkdown: string }) => {
      const res = await fetch('/v1/notes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify({
          scope: 'COMPLIANCE_ITEM',
          scopeId: id,
          ...body,
        }),
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notes', { scope: 'COMPLIANCE_ITEM', scopeId: id }] }),
  });

  function userName(uid: string | null) {
    if (!uid) return '—';
    const u = users.data?.find((x) => x.id === uid);
    return u ? `${u.displayName}` : uid.slice(0, 8);
  }

  if (item.isLoading) return <div className="text-muted p-8 text-center">Loading compliance item…</div>;
  if (item.error && isHttpStatus(item.error, 403, 404)) {
    return <ForbiddenPage title="Not found or no access" message="This compliance item doesn't exist or you don't have permission to view it." />;
  }
  if (item.error) return <div className="text-rose-400 p-8 text-center">{(item.error as Error).message}</div>;
  if (!item.data) return <div className="text-muted p-8 text-center">Item not found.</div>;

  const i = item.data;
  const isAssignee = !!currentUser && (i.assignee1Id === currentUser.id || i.assignee2Id === currentUser.id);
  const unlocked = i.status !== 'Completed' || canDo('compliance.edit_completed');
  const canManage = unlocked && (isAssignee || canDo('compliance.review'));
  const canEdit = unlocked && canDo('compliance.edit');
  const canReview = unlocked && canDo('compliance.review');

  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between">
        <div>
          <button onClick={() => navigate('/compliance')} className="text-xs text-muted hover:text-white mb-1">← Back to Compliance</button>
          <h1 className="text-xl font-semibold text-white">{i.serviceType}</h1>
          <p className="text-sm text-muted">
            {client.data ? (
              <Link to={`/compliance?clientId=${i.clientId}`} className="hover:text-accent underline">{client.data.company ?? client.data.name}</Link>
            ) : client.isLoading ? '…' : '—'}
            {' · '}
            {i.jurisdiction ? (JURISDICTION_LABELS[i.jurisdiction] ?? i.jurisdiction) : '—'}
            {' · '}
            <StatusPill status={i.status} />
            {i.dataStatus && (
              <>
                {' · '}
                <DataStatusPill status={i.dataStatus} />
              </>
            )}
          </p>
          <div className="mt-2">
            <EntityTags scope="COMPLIANCE_ITEM" entityId={i.id} />
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit ? (
            <button
              onClick={() => setShowEdit(true)}
              className="text-sm text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg"
            >
              Edit
            </button>
          ) : (
            <span className="text-xs text-muted self-center">Editing requires permission and an unlocked item</span>
          )}
          <button
            disabled={!canEdit}
            onClick={() => setShowDelete(true)}
            className="text-sm btn-ghost-danger border px-3 py-1.5 rounded-lg"
          >
            Delete
          </button>
        </div>
      </header>

      <div className="border-b border-border">
        <nav className="flex gap-1">
          {(['overview', 'notes', 'time'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium capitalize ${tab === t ? 'text-accent border-b-2 border-accent' : 'text-muted hover:text-white'}`}
            >
              {t}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="bg-bg-panel border border-border rounded-xl p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Status" value={<StatusPill status={i.status} />} />
            <Field label="Data status" value={i.dataStatus ? <DataStatusPill status={i.dataStatus} /> : '—'} />
            <Field label="Jurisdiction" value={i.jurisdiction ? (JURISDICTION_LABELS[i.jurisdiction] ?? i.jurisdiction) : '—'} />
            <Field label="Recurring" value={i.isRecurring ? (i.recurrenceFrequency ?? 'Yes') : 'No'} />
            <Field label="End date (period end)" value={i.deadline ? fmtDate(i.deadline) : '—'} />
            {i.taxPeriod && <>
              <Field label="Tax period start" value={fmtDate(i.taxPeriod.startDate)} />
              <Field label="Tax period end" value={fmtDate(i.taxPeriod.endDate)} />
              <Field label="Tax deadline source" value={i.taxPeriod.deadlineSource} />
            </>}
            <Field label="Due date" value={i.dueDate ? fmtDate(i.dueDate) : '—'} />
            <Field label="Next due" value={i.nextDueAt ? fmtDate(i.nextDueAt) : '—'} />
            <Field label="Completed at" value={i.completedAt ? fmtDate(i.completedAt) : '—'} />
            <Field label="Sent back count" value={i.sentBackCount.toString()} />
            <Field label="Assignee 1" value={userName(i.assignee1Id)} />
            <Field label="Assignee 2" value={userName(i.assignee2Id)} />
            <Field label="Manager" value={userName(i.managerId)} />
            <Field label="Est. hours" value={i.estimatedHours != null ? i.estimatedHours.toFixed(2) : '—'} />
          </div>

          <div className="bg-bg-panel border border-border rounded-xl p-5">
            <h2 className="text-sm font-medium text-white mb-3">Actions</h2>
            <div className="flex flex-wrap gap-2">
              {canManage && i.status !== 'ReadyForReview' && i.status !== 'SentToClient' && i.status !== 'Completed' && (
                <ActionButton onClick={() => action.mutate('ready_for_review')} loading={action.isPending && action.variables === 'ready_for_review'}>Ready for review</ActionButton>
              )}
              {canReview && i.status !== 'SentToClient' && i.status !== 'Completed' && (
                <ActionButton onClick={() => action.mutate('send_to_client')} loading={action.isPending && action.variables === 'send_to_client'}>Send to client</ActionButton>
              )}
              {canReview && i.status !== 'SentBack' && i.status !== 'Completed' && (
                <ActionButton onClick={() => action.mutate('send_back')} loading={action.isPending && action.variables === 'send_back'}>Send back</ActionButton>
              )}
              {canReview && canDo('compliance.complete') && i.status !== 'Completed' && (
                <ActionButton onClick={() => action.mutate('mark_completed')} loading={action.isPending && action.variables === 'mark_completed'}>Mark completed</ActionButton>
              )}
              {canReview && (!i.isRecurring ? (
                <ActionButton onClick={() => action.mutate('enable')} loading={action.isPending && action.variables === 'enable'}>Enable recurrence</ActionButton>
              ) : (
                <ActionButton onClick={() => action.mutate('pause')} loading={action.isPending && action.variables === 'pause'}>Pause recurrence</ActionButton>
              ))}
              {!canEdit && (
                <span className="text-xs text-muted self-center">Editing requires permission and an unlocked item</span>
              )}
            </div>
            {action.error && <p className="mt-2 text-xs text-rose-400">{(action.error as Error).message}</p>}
          </div>
        </div>
      )}

      {tab === 'notes' && (
        <div className="space-y-4">
          <CreateNoteForm onSubmit={createNote.mutate} submitting={createNote.isPending} error={createNote.error?.message ?? null} />
          <DataList
            isLoading={notes.isLoading}
            error={notes.error as Error | null}
            items={notes.data ?? []}
            empty="No notes yet."
            render={(n) => (
              <div className="border-b border-border/50 last:border-0 px-4 py-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-white">{n.title ?? 'Note'}</span>
                  <span className="text-xs text-muted">{fmtDate(n.createdAt)} · {userName(n.authorId)}</span>
                </div>
                <p className="text-sm text-white whitespace-pre-wrap">{n.bodyMarkdown}</p>
              </div>
            )}
          />
        </div>
      )}

      {tab === 'time' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-medium text-white">Time entries</h2>
            <p className="text-xs text-muted">Log time from the global Time module.</p>
          </div>
          <DataList
            isLoading={timeEntries.isLoading}
            error={timeEntries.error as Error | null}
            items={timeEntries.data?.items ?? []}
            empty="No time logged yet."
            render={(t) => (
              <div className="border-b border-border/50 last:border-0 px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm text-white">{t.hours.toFixed(2)}h · {t.description ?? 'No description'}</p>
                  <p className="text-xs text-muted">{fmtDate(t.date)} · {userName(t.userId)} · {t.billable ? 'Billable' : 'Non-billable'}{t.invoiceId ? ' · Invoiced' : ''}</p>
                </div>
              </div>
            )}
          />
        </div>
      )}

      {showEdit && item.data && (
        <ComplianceItemFormModal
          mode="edit"
          item={item.data}
          onClose={() => setShowEdit(false)}
          onSubmit={(patch) => update.mutate(patch)}
          error={update.error?.message ?? null}
          submitting={update.isPending}
        />
      )}
      {showDelete && item.data && (
        <ConfirmModal
          title="Delete compliance item"
          message={`Soft-delete "${item.data.serviceType}"?`}
          onClose={() => setShowDelete(false)}
          onConfirm={() => remove.mutate()}
          error={remove.error?.message ?? null}
          submitting={remove.isPending}
          confirmText="Delete"
          confirmClass="bg-rose-700 hover:bg-rose-600 text-oncolor"
        />
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <span className="text-xs text-muted uppercase tracking-wide">{label}</span>
      <p className="text-sm text-white mt-1">{value}</p>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const classes = STATUS_BADGE_CLASSES[status as keyof typeof STATUS_BADGE_CLASSES] ?? 'pill-neutral';
  const label = ITEM_STATUS_LABELS[status as keyof typeof ITEM_STATUS_LABELS] ?? status;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium ${classes}`}>{label}</span>;
}

function DataStatusPill({ status }: { status: string }) {
  const classes = DATA_STATUS_BADGE_CLASSES[status as keyof typeof DATA_STATUS_BADGE_CLASSES] ?? 'pill-neutral';
  const label = DATA_STATUS_LABELS[status as keyof typeof DATA_STATUS_LABELS] ?? status;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium ${classes}`}>{label}</span>;
}

function ActionButton({ children, onClick, loading }: { children: React.ReactNode; onClick: () => void; loading?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="text-sm text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg disabled:opacity-50"
    >
      {loading ? 'Working…' : children}
    </button>
  );
}

function DataList<T>({ isLoading, error, items, empty, render }: { isLoading: boolean; error: Error | null; items: T[]; empty: string; render: (item: T) => React.ReactNode }) {
  if (isLoading) return <div className="text-muted p-6 text-center bg-bg-panel border border-border rounded-xl">Loading…</div>;
  if (error) return <div className="text-rose-400 p-6 text-center bg-bg-panel border border-border rounded-xl">{error.message}</div>;
  if (items.length === 0) return <div className="text-muted p-6 text-center bg-bg-panel border border-border rounded-xl">{empty}</div>;
  return <div className="bg-bg-panel border border-border rounded-xl overflow-hidden">{items.map((item, idx) => <div key={idx}>{render(item)}</div>)}</div>;
}

function CreateNoteForm({ onSubmit, submitting, error }: { onSubmit: (input: { title?: string; bodyMarkdown: string }) => void; submitting: boolean; error: string | null }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  return (
    <div className="bg-bg-panel border border-border rounded-xl p-5">
      <h2 className="text-sm font-medium text-white mb-3">Add note</h2>
      <div className="space-y-3">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted focus:outline-none focus:border-accent" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Note body (markdown supported)" rows={3} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted focus:outline-none focus:border-accent resize-none" />
        {error && <p className="text-xs text-rose-400">{error}</p>}
        <div className="flex justify-end">
          <button
            onClick={() => { onSubmit({ title: title || undefined, bodyMarkdown: body }); setTitle(''); setBody(''); }}
            disabled={submitting || !body.trim()}
            className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
          >
            {submitting ? 'Saving…' : 'Add note'}
          </button>
        </div>
      </div>
    </div>
  );
}

